import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Circle, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Layers, AlertTriangle, Flame } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const hazColor = h => h > 40 ? C.red : h > 20 ? C.amber : C.green;
const fmt$ = v => `$${Math.round(v || 0).toLocaleString()}`;

const FILTERS = [
  { id: 'all', label: 'All' }, { id: 'submission', label: 'Quotes' }, { id: 'policy', label: 'Policies' },
];
const HAZ_FILTERS = [
  { id: 'all', label: 'Any hazard' }, { id: 'high', label: 'High (>40)' }, { id: 'medium', label: 'Medium (20-40)' }, { id: 'low', label: 'Low (<20)' },
];

export default function RiskMapPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('all');
  const [haz, setHaz] = useState('all');
  const [country, setCountry] = useState('all');
  const [showClaims, setShowClaims] = useState(false);
  const [claimsData, setClaimsData] = useState(null);

  useEffect(() => {
    api.get('/risk-map/points').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    if (showClaims && !claimsData) {
      api.get('/risk-map/claims').then(r => setClaimsData(r.data)).catch(() => {});
    }
  }, [showClaims, claimsData, api]);

  const points = useMemo(() => {
    let pts = data?.points || [];
    if (type !== 'all') pts = pts.filter(p => p.type === type);
    if (haz === 'high') pts = pts.filter(p => (p.hazard_score || 0) > 40);
    else if (haz === 'medium') pts = pts.filter(p => (p.hazard_score || 0) > 20 && (p.hazard_score || 0) <= 40);
    else if (haz === 'low') pts = pts.filter(p => (p.hazard_score || 0) <= 20);
    if (country !== 'all') pts = pts.filter(p => p.country === country);
    return pts;
  }, [data, type, haz, country]);

  if (loading) return <div data-testid="risk-map-loading"><Skeleton className="h-96 rounded-sm" /></div>;

  const acc = data?.accumulation || [];
  const totalTSI = points.reduce((s, p) => s + (p.sum_insured || 0), 0);
  const highCount = points.filter(p => (p.hazard_score || 0) > 40).length;

  return (
    <div className="space-y-4" data-testid="risk-map-page">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: 'rgba(136,150,168,0.25)' }}>
          {FILTERS.map(f => (
            <button key={f.id} data-testid={`map-filter-${f.id}`} onClick={() => setType(f.id)}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={type === f.id ? { background: C.blue, color: '#fff' } : { color: C.slate, background: '#fff' }}>{f.label}</button>
          ))}
        </div>
        <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: 'rgba(136,150,168,0.25)' }}>
          {HAZ_FILTERS.map(f => (
            <button key={f.id} data-testid={`map-haz-${f.id}`} onClick={() => setHaz(f.id)}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={haz === f.id ? { background: C.navy, color: '#fff' } : { color: C.slate, background: '#fff' }}>{f.label}</button>
          ))}
        </div>
        <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: 'rgba(136,150,168,0.25)' }}>
          {['all', 'AU', 'NZ'].map(cid => (
            <button key={cid} data-testid={`map-country-${cid}`} onClick={() => setCountry(cid)}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={country === cid ? { background: C.teal, color: '#fff' } : { color: C.slate, background: '#fff' }}>{cid === 'all' ? 'AU + NZ' : cid}</button>
          ))}
        </div>
        <button data-testid="toggle-claims-layer" onClick={() => setShowClaims(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors"
          style={showClaims ? { background: C.red, color: '#fff', borderColor: C.red }
            : { color: C.slate, background: '#fff', borderColor: 'rgba(136,150,168,0.25)' }}>
          <Flame className="w-3.5 h-3.5" />Claims heat
        </button>
        <span className="ml-auto flex items-center gap-3 text-xs" style={{ color: C.slate }} data-testid="map-summary">
          <span><b style={{ color: C.navy }}>{points.length.toLocaleString()}</b> pins</span>
          <span>TSI <b style={{ color: C.navy }}>{fmt$(totalTSI)}</b></span>
          <span style={{ color: highCount ? C.red : C.green }}><AlertTriangle className="w-3 h-3 inline mr-0.5" />{highCount} high hazard</span>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-3 rounded-sm overflow-hidden border" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid="risk-map-container">
          <MapContainer center={[-31.5, 155]} zoom={4} style={{ height: 560, width: '100%' }} scrollWheelZoom>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
            {showClaims && (claimsData?.cells || []).map((cell, i) => (
              <Circle key={`heat-${i}`} center={[cell.lat, cell.lng]}
                radius={12000 + 30000 * Math.sqrt(cell.intensity)}
                pathOptions={{ color: 'transparent', fillColor: '#E24B4A',
                  fillOpacity: 0.12 + 0.35 * cell.intensity }}>
                <Popup>
                  <div style={{ fontSize: 12 }}>
                    <b style={{ color: C.red }}>Claims hotspot</b>
                    <div>{cell.claims} claim{cell.claims === 1 ? '' : 's'}</div>
                    <div>Incurred: <b>{fmt$(cell.incurred)}</b></div>
                  </div>
                </Popup>
              </Circle>
            ))}
            {points.map(p => (
              <CircleMarker key={p.id} center={[p.lat, p.lng]}
                radius={p.type === 'policy' ? 4 : 6}
                pathOptions={{ color: hazColor(p.hazard_score), fillColor: hazColor(p.hazard_score),
                  fillOpacity: p.type === 'policy' ? 0.35 : 0.75, weight: 1 }}>
                <Popup>
                  <div style={{ fontSize: 12, minWidth: 190 }}>
                    <b>{p.business_name}</b>
                    <div style={{ color: C.slate, fontSize: 10, textTransform: 'uppercase', margin: '2px 0' }}>
                      {p.type === 'policy' ? `Policy · ${p.class || ''}` : `Quote · ${p.decision || ''}`}
                    </div>
                    <div>Sum insured: <b>{fmt$(p.sum_insured)}</b></div>
                    {p.premium != null && <div>Premium: <b>{fmt$(p.premium)}</b></div>}
                    <div>Hazard score: <b style={{ color: hazColor(p.hazard_score) }}>{p.hazard_score ?? '—'}/100</b></div>
                    {p.zones && <div style={{ color: C.slate, fontSize: 10, marginTop: 2 }}>{p.zones}</div>}
                    <div style={{ color: C.slate, fontSize: 10 }}>{[p.suburb, p.state].filter(Boolean).join(', ')}</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </Card>

        <div className="space-y-3">
          <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid="accumulation-panel">
            <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.navy }}>
              <Layers className="w-3.5 h-3.5" style={{ color: C.blue }} />Accumulation by Region
            </h3>
            {acc.slice(0, 10).map(a => (
              <div key={a.region} className="py-1.5 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.08)' }}
                data-testid={`accumulation-${a.region}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium" style={{ color: C.navy }}>{a.region}</span>
                  <span className="font-mono font-semibold" style={{ color: C.navy }}>{fmt$(a.tsi)}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]" style={{ color: C.slate }}>
                  <span>{a.count} risks · GWP {fmt$(a.gwp)}</span>
                  <span style={{ color: a.avg_hazard > 40 ? C.red : a.avg_hazard > 20 ? C.amber : C.green }}>haz {a.avg_hazard}</span>
                </div>
                <div className="h-1 mt-1 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.12)' }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, (a.tsi / (acc[0]?.tsi || 1)) * 100)}%`, background: C.blue }} />
                </div>
              </div>
            ))}
          </Card>
          <Card className="p-3 rounded-sm border bg-white text-[10px]" style={{ borderColor: 'rgba(136,150,168,0.2)', color: C.slate }}>
            <p className="flex items-center gap-1 font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.navy }}>
              <MapPin className="w-3 h-3" />Legend
            </p>
            <p><span style={{ color: C.green }}>●</span> Low hazard (&lt;20) &nbsp; <span style={{ color: C.amber }}>●</span> Medium (20-40) &nbsp; <span style={{ color: C.red }}>●</span> High (&gt;40)</p>
            <p className="mt-1">Large solid pins = quoted submissions with geocoded addresses. Small translucent pins = bound policies (register coordinates).</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
